import { useContext, useEffect, useState } from 'react';
import { useLazyDataModel } from '@thd-nucleus/data-sources';
import { useConfigService, useStoreId, ExperienceContext } from '@thd-nucleus/experience-context';

/**
 * @typedef {Object} UserFeedbackReturn
 * @property {boolean} feedbackSubmitted - Indicates if feedback has been submitted.
 * @property {Function} handleSelectOption - Function to handle option selection.
 * @property {string[]} selectedOptionReasons - Reasons for the selected feedback option.
 * @property {boolean} showFeedbackForm - Indicates if the feedback form is shown.
 * @property {Function} handleFeedbackFormClose - Function to handle the feedback form close.
 * @property {Function} handleFeedbackFormSubmit - Function to handle the feedback form submission.
 */
/**
 * Hook to manage feedback form state and submission.
 * @param {Object} params - Parameters for the hook.
 * @param {string} params.anchorKey - Key for the feedback anchor.
 * @param {string} params.anchorType - Type of the feedback anchor.
 * @param {boolean} params.skipFeedbackForm - Flag to skip the feedback form.
 * @param {Object} params.reasons - Reasons for feedback. { POSITIVE: string[], NEGATIVE: string[] }
 * @param {Object} params.customerInsights - Customer insights name.
 * @param {Function} params.handleFeedbackData - Function to handle feedback data.
 * @returns { UserFeedbackReturn } - Returns an object containing feedback form state and handlers.
 */
export const useUserFeedback = ({
  anchorKey,
  anchorType,
  skipFeedbackForm,
  reasons,
  customerInsights,
  handleFeedbackData,
}) => {
  const storeId = useStoreId();
  const { deliveryZipCode } = useContext(ExperienceContext);
  const [optionSelected, setOptionSelected] = useState(false);
  const [feedbackSubmitted, setFeedbackSubmitted] = useState(false);
  const [selectedOptionReasons, setSelectedOptionReasons] = useState([]);
  const [showFeedbackForm, setShowFeedbackForm] = useState(false);
  const [submitUserFeedback, response] = useLazyDataModel('captureFeedback', {
    lazy: true
  });

  const llmFromQABotFS = useConfigService('fs-prop:magic-apron-bot-llm');
  const llmFromMAABotFS = useConfigService('fs:magic-apron-assistant-bot-llm');
  const llmFromFS = customerInsights === 'Assistant' ? llmFromMAABotFS : llmFromQABotFS || '';

  const getFeedbackData = (formData) => {
    const visitorId = window?.THDCustomer?.default?.mcvisID;
    return {
      feedback: {
        feedbackComment: '',
        feedbackReason: '',
        modelName: llmFromFS,
        ...formData,
        storeId,
        visitorId,
        zipCode: deliveryZipCode
      }
    };
  };

  const submitFeedbackData = (feedback) => {
    const dataToSubmit = {
      ...feedback,
      anchorKey,
      anchorType,
      customerInsights,
      content: handleFeedbackData()
    };
    submitUserFeedback({ variables: getFeedbackData(dataToSubmit) });
    if (response) {
      setFeedbackSubmitted(true);
    }
  };

  const handleSelectOption = (option) => setOptionSelected(option);

  const handleAfterThumbsUpThumbsDownSelected = () => {
    if (reasons[optionSelected].length === 0 || skipFeedbackForm) {
      submitFeedbackData({ feedbackType: optionSelected });
      setFeedbackSubmitted(true);
    }

    setSelectedOptionReasons(reasons[optionSelected]);
    setShowFeedbackForm(true);
  };

  const handleFeedbackFormClose = () => {
    setShowFeedbackForm(false);
    submitFeedbackData({ feedbackType: optionSelected });
    setFeedbackSubmitted(true);
  };

  const handleFeedbackFormSubmit = (data) => {
    submitFeedbackData({ feedbackType: optionSelected, ...data });
    setFeedbackSubmitted(true);
  };

  useEffect(() => {
    if ((optionSelected)) handleAfterThumbsUpThumbsDownSelected();
  }, [optionSelected]);

  return {
    feedbackSubmitted,
    handleSelectOption,
    selectedOptionReasons,
    showFeedbackForm,
    handleFeedbackFormClose,
    handleFeedbackFormSubmit,
  };
};
